import React from 'react'
import { BuildingIcon } from 'lucide-react'
import { AddressAutocomplete } from '../AddressAutocomplete'
import { formatPhoneInput } from './phoneFormat'
import type { CustomerCompanyData } from './types'

interface CustomerCompanyFieldsProps {
  value: CustomerCompanyData
  onChange: (value: CustomerCompanyData) => void
  compact?: boolean
}

export function CustomerCompanyFields({ value, onChange, compact }: CustomerCompanyFieldsProps) {
  const inputClass = compact
    ? 'w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm'
    : 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

  const set = (patch: Partial<CustomerCompanyData>) => onChange({ ...value, ...patch })

  return (
    <div>
      {!compact && (
        <div className="flex items-center gap-2 mb-6">
          <BuildingIcon className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Company Information</h2>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
          <input
            type="text"
            required
            value={value.name}
            onChange={(e) => set({ name: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
          <input
            type="tel"
            value={value.phone}
            onChange={(e) => set({ phone: formatPhoneInput(e.target.value) })}
            placeholder="(123) 456-7890"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
          <input
            type="email"
            value={value.email}
            onChange={(e) => set({ email: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
          <input
            type="text"
            value={value.website}
            onChange={(e) => set({ website: e.target.value })}
            placeholder="www.example.com or example.com"
            className={inputClass}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
          <AddressAutocomplete
            value={value.address}
            onChange={(address) => set({ address })}
            onAddressSelect={(addressData) =>
              set({
                address: addressData.address,
                city: addressData.city,
                state: addressData.state,
                zip: addressData.zip,
              })
            }
            placeholder="Start typing address..."
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Address Line 2 <span className="text-gray-400 font-normal">(Optional)</span>
          </label>
          <input
            type="text"
            value={value.address_line_2}
            onChange={(e) => set({ address_line_2: e.target.value })}
            placeholder="Office #, Suite, Unit, etc."
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
          <input
            type="text"
            value={value.city}
            onChange={(e) => set({ city: e.target.value })}
            placeholder="City"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
          <input
            type="text"
            value={value.state}
            onChange={(e) => set({ state: e.target.value })}
            placeholder="NY"
            maxLength={2}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Zip Code</label>
          <input
            type="text"
            value={value.zip}
            onChange={(e) => set({ zip: e.target.value })}
            placeholder="10001"
            className={inputClass}
          />
        </div>
      </div>
    </div>
  )
}
